/**
 * Audience definition AST (KTD6).
 *
 * A stored audience is a tree of `and` / `or` / `not` nodes over predicates.
 * Plans 4 and 5 evaluate it; this file fixes its shape and limits:
 *
 * - every array holds 1 to 500 entries, `and` / `or` hold 1 to 20 children;
 * - the whole tree has at most 50 nodes and depth 6 (the root is depth 1);
 * - every object is strict (unknown keys fail);
 * - `guardians_of` cannot nest inside `guardians_of`;
 * - there is no engagement predicate on opens (R47).
 *
 * `buildAudienceDefinitionSchema(types)` builds the same schema over a subset
 * of predicate types, so another domain's rule (the pages audience) is always
 * a valid comms definition too.
 */
import { z } from 'zod'

export const AUDIENCE_LIST_MAX = 500
export const AUDIENCE_MAX_NODES = 50
export const AUDIENCE_MAX_DEPTH = 6
export const AUDIENCE_MAX_CHILDREN = 20

const list = <T extends z.ZodType>(item: T) => z.array(item).min(1).max(AUDIENCE_LIST_MAX)

const NonEmptyStringSchema = z.string().min(1)

export const AudienceMemberRoleSchema = z.enum(['PARENT', 'STUDENT', 'FACULTY', 'STAFF'])
export type AudienceMemberRole = z.infer<typeof AudienceMemberRoleSchema>

/** `Section.offeringType` values, as KTD6 writes them. */
export const AudienceSectionOfferingTypeSchema = z.enum(['ACADEMIC', 'ADVISORY', 'ACTIVITY', 'ATHLETICS'])
export type AudienceSectionOfferingType = z.infer<typeof AudienceSectionOfferingTypeSchema>

/**
 * Format of an admissions-CRM stage or disposition key. Only the format is
 * checked here: the admissions domain validates the keys themselves.
 */
export const AdmissionsKeySchema = z.string().regex(/^[A-Z][A-Z0-9_]{0,63}$/)

export const AudiencePredicateTypeSchema = z.enum([
  'role',
  'grade',
  'division',
  'graduation_year',
  'section',
  'custom_group',
  'bb_list',
  'admissions_stage',
  'app_activation',
  'has_email',
  'primary_language',
  'guardians_of',
  'ext'
])
export type AudiencePredicateType = z.infer<typeof AudiencePredicateTypeSchema>

// ---------------------------------------------------------------------------
// Leaf predicates (everything except the recursive `guardians_of`)
// ---------------------------------------------------------------------------

export const RolePredicateSchema = z.strictObject({
  type: z.literal('role'),
  roles: list(AudienceMemberRoleSchema)
})

/** Grade labels as synced on the student profile. */
export const GradePredicateSchema = z.strictObject({
  type: z.literal('grade'),
  gradeLevels: list(NonEmptyStringSchema)
})

/** Division names as synced. */
export const DivisionPredicateSchema = z.strictObject({
  type: z.literal('division'),
  divisions: list(NonEmptyStringSchema)
})

/** `SchoolUserProfile.graduationYear` is a string. */
export const GraduationYearPredicateSchema = z.strictObject({
  type: z.literal('graduation_year'),
  years: list(NonEmptyStringSchema)
})

export const SectionPredicateSchema = z.strictObject({
  type: z.literal('section'),
  sectionPublicIds: list(NonEmptyStringSchema),
  memberRoles: list(AudienceMemberRoleSchema).optional(),
  offeringTypes: list(AudienceSectionOfferingTypeSchema).optional()
})

export const CustomGroupPredicateSchema = z.strictObject({
  type: z.literal('custom_group'),
  groupPublicIds: list(NonEmptyStringSchema)
})

export const BbListPredicateSchema = z.strictObject({
  type: z.literal('bb_list'),
  listPublicIds: list(NonEmptyStringSchema)
})

/**
 * An applicant (one child for one entering year). Needs at least one of
 * `stages`, `rawStatusIds` or `dispositions`. An absent `dispositions` means
 * `['ACTIVE']`, applied by the evaluator, not here.
 */
export const AdmissionsStagePredicateSchema = z
  .strictObject({
    type: z.literal('admissions_stage'),
    stages: list(AdmissionsKeySchema).optional(),
    dispositions: list(AdmissionsKeySchema).optional(),
    enteringYear: NonEmptyStringSchema.optional(),
    enteringGrades: list(NonEmptyStringSchema).optional(),
    rawStatusIds: list(z.number().int()).optional()
  })
  .refine(p => p.stages !== undefined || p.rawStatusIds !== undefined || p.dispositions !== undefined, {
    message: 'admissions_stage needs at least one of stages, rawStatusIds or dispositions'
  })

export const AppActivationPredicateSchema = z.strictObject({
  type: z.literal('app_activation'),
  activated: z.boolean()
})

export const HasEmailPredicateSchema = z.strictObject({
  type: z.literal('has_email'),
  value: z.boolean()
})

export const PrimaryLanguagePredicateSchema = z.strictObject({
  type: z.literal('primary_language'),
  languages: list(NonEmptyStringSchema)
})

/** A predicate registered by another domain; plan 4 validates `params`. */
export const ExtPredicateSchema = z.strictObject({
  type: z.literal('ext'),
  key: NonEmptyStringSchema,
  params: z.record(z.string(), z.unknown())
})

type LeafPredicate =
  | z.infer<typeof RolePredicateSchema>
  | z.infer<typeof GradePredicateSchema>
  | z.infer<typeof DivisionPredicateSchema>
  | z.infer<typeof GraduationYearPredicateSchema>
  | z.infer<typeof SectionPredicateSchema>
  | z.infer<typeof CustomGroupPredicateSchema>
  | z.infer<typeof BbListPredicateSchema>
  | z.infer<typeof AdmissionsStagePredicateSchema>
  | z.infer<typeof AppActivationPredicateSchema>
  | z.infer<typeof HasEmailPredicateSchema>
  | z.infer<typeof PrimaryLanguagePredicateSchema>
  | z.infer<typeof ExtPredicateSchema>

type LeafPredicateType = Exclude<AudiencePredicateType, 'guardians_of'>

const LEAF_PREDICATE_SCHEMAS: Record<LeafPredicateType, z.ZodType<LeafPredicate>> = {
  role: RolePredicateSchema,
  grade: GradePredicateSchema,
  division: DivisionPredicateSchema,
  graduation_year: GraduationYearPredicateSchema,
  section: SectionPredicateSchema,
  custom_group: CustomGroupPredicateSchema,
  bb_list: BbListPredicateSchema,
  admissions_stage: AdmissionsStagePredicateSchema,
  app_activation: AppActivationPredicateSchema,
  has_email: HasEmailPredicateSchema,
  primary_language: PrimaryLanguagePredicateSchema,
  ext: ExtPredicateSchema
}

// ---------------------------------------------------------------------------
// Tree types
// ---------------------------------------------------------------------------

/** Guardians whose eligible, current edge reaches a student matching `students` (R10). */
export type GuardiansOfPredicate = { type: 'guardians_of'; students: AudienceNode }

export type AudiencePredicate = LeafPredicate | GuardiansOfPredicate

export type AudienceNode =
  | { op: 'and'; children: AudienceNode[] }
  | { op: 'or'; children: AudienceNode[] }
  | { op: 'not'; child: AudienceNode }
  | AudiencePredicate

export type AudienceDefinition = { version: 1; root: AudienceNode }

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

function buildNode(allowed: ReadonlySet<AudiencePredicateType>): z.ZodType<AudienceNode> {
  const node: z.ZodType<AudienceNode> = z.lazy(() => {
    const options: z.ZodType<AudienceNode>[] = [
      z.strictObject({
        op: z.literal('and'),
        children: z.array(node).min(1).max(AUDIENCE_MAX_CHILDREN)
      }),
      z.strictObject({
        op: z.literal('or'),
        children: z.array(node).min(1).max(AUDIENCE_MAX_CHILDREN)
      }),
      z.strictObject({ op: z.literal('not'), child: node })
    ]
    for (const type of AudiencePredicateTypeSchema.options) {
      if (!allowed.has(type)) continue
      if (type === 'guardians_of') {
        // `students` uses the same subset without `guardians_of`, so a
        // nested `guardians_of` fails at any depth.
        const inner = new Set(allowed)
        inner.delete('guardians_of')
        options.push(z.strictObject({ type: z.literal('guardians_of'), students: buildNode(inner) }))
      } else {
        options.push(LEAF_PREDICATE_SCHEMAS[type])
      }
    }
    return z.union(options)
  })
  return node
}

type TreeSize = { nodes: number; depth: number }

/** Counts every op node and predicate, and the deepest level (root = 1). */
function measureTree(root: AudienceNode): TreeSize {
  let nodes = 0
  let depth = 0
  const walk = (n: AudienceNode, level: number): void => {
    // Defensive: only a structurally valid tree reaches here, but never throw.
    if (typeof n !== 'object' || n === null) return
    nodes += 1
    if (level > depth) depth = level
    if ('op' in n) {
      if (n.op === 'not') walk(n.child, level + 1)
      else if (Array.isArray(n.children)) for (const child of n.children) walk(child, level + 1)
    } else if (n.type === 'guardians_of') {
      walk(n.students, level + 1)
    }
  }
  walk(root, 1)
  return { nodes, depth }
}

/**
 * A definition schema that allows only the listed predicate types, with the
 * same `and` / `or` / `not` nodes and limits as `AudienceDefinitionSchema`.
 */
export function buildAudienceDefinitionSchema(types: readonly AudiencePredicateType[]): z.ZodType<AudienceDefinition> {
  return z.strictObject({ version: z.literal(1), root: buildNode(new Set(types)) }).superRefine((definition, ctx) => {
    const { nodes, depth } = measureTree(definition.root)
    if (nodes > AUDIENCE_MAX_NODES) {
      ctx.addIssue({
        code: 'custom',
        path: ['root'],
        message: `Audience definition has ${nodes} nodes; the limit is ${AUDIENCE_MAX_NODES}`
      })
    }
    if (depth > AUDIENCE_MAX_DEPTH) {
      ctx.addIssue({
        code: 'custom',
        path: ['root'],
        message: `Audience definition is ${depth} levels deep; the limit is ${AUDIENCE_MAX_DEPTH}`
      })
    }
  })
}

export const AudienceDefinitionSchema = buildAudienceDefinitionSchema(AudiencePredicateTypeSchema.options)
